import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import ContentPasteSearchIcon from "@mui/icons-material/ContentPasteSearch";
import EditNoteIcon from "@mui/icons-material/Edit";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import SyncIcon from "@mui/icons-material/Sync";

import RequestAssignmentApi from "../../../../../infrastructure/api/requestAssignment";
import CustomerRequestLocationService from "../../../../../infrastructure/api/customerRequestLocation";
import useCheckPermission from "../../../../common/hooks/useCheckPermission";
import useGetPageInfo from "../../../../common/hooks/useGetPageInfo";
import AssignmentOperatorHelp from "../../../../components/request/AssignmentOperatorHelp";
import {
  assignmentOwnerColumn,
  canShowClaimAction,
  getAssignmentUserContext,
} from "../../../../components/request/requestAssignmentDisplay";
import RequestFollowUpDialog from "../../../../components/request/RequestFollowUpDialog";
import { useAssignmentFilteredList } from "../../../../components/request/useAssignmentFilteredList";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";
import EnhancedTable from "../../../../components/shared/table";
import CheckNewRequestMoreInfoModal from "./CheckNewRequestMoreInfoModal";
import {
  buildCheckNewRequestModifyPath,
  getRequestRowId,
  openCheckNewRequestModifyPath,
} from "../../shared/checkNewRequest/checkNewRequestPageUtils";

import {
  buildLocationLookupMaps,
  enrichApplicantIdentityFields,
  resolveRowCityName,
  resolveRowProvinceName,
} from "../all-requests/allRequestsPageUtils";
import PartnerReferralFilterSelect from "../shared/PartnerReferralFilterSelect";
import {
  matchesPartnerReferralFilter,
  usePartnerReferralFilter,
} from "../shared/partnerReferralFilterUtils";

const enrichCheckNewRows = (list, locationMaps = null) =>
  (Array.isArray(list) ? list : []).map((row) => ({
    ...enrichApplicantIdentityFields(row),
    id: getRequestRowId(row),
    provinceName: resolveRowProvinceName(row, locationMaps),
    cityName: resolveRowCityName(row, locationMaps),
    installableAreaSquareMeterDisplay:
      row.installableAreaSquareMeter != null
        ? row.installableAreaSquareMeter
        : "ثبت نشده",
    partnerReferralDisplayName:
      row.partnerReferralDisplayName ??
      row.PartnerReferralDisplayName ??
      "",
    partnerReferralLevel1PartnerId:
      row.partnerReferralLevel1PartnerId ??
      row.PartnerReferralLevel1PartnerId ??
      null,
  }));

const SolarentOperatorCheckNewRequestPage = ({ hubMode = false } = {}) => {
  const pageInfo = useGetPageInfo();
  const theme = useTheme();
  const isCompactViewport = useMediaQuery(theme.breakpoints.down("md"));
  const canEdit = useCheckPermission("edit");
  const [partnerReferralFilter, setPartnerReferralFilter] =
    usePartnerReferralFilter(hubMode);

  const {
    rows: dataList,
    assignmentFilter,
    setAssignmentFilter,
    filterOptions: assignmentFilterOptions,
    showAssignmentUi,
    featureChecked,
    canClaimRequests,
    currentUserRoleName,
    loading,
    reloadList,
  } = useAssignmentFilteredList("SLN/SolarentOperator/CheckNewRequest", false, {
    // صف بررسی اولیه: همه درخواست‌های قابل بررسی (مثل مدیر)، نه فقط «من + بدون مسئول»
    defaultAssignmentFilter: "all",
  });

  const [selectedPowerPlantType, setSelectedPowerPlantType] = useState("");
  const [selectedLocationUsage, setSelectedLocationUsage] = useState("");
  const [selectedLeadPriority, setSelectedLeadPriority] = useState("");
  const [selectedFollowUpStatus, setSelectedFollowUpStatus] = useState("");
  const [selectedApplicantGoal, setSelectedApplicantGoal] = useState("");
  const [minArea, setMinArea] = useState("");
  const [maxArea, setMaxArea] = useState("");
  const [followUpRequestId, setFollowUpRequestId] = useState(null);
  const [moreInfoRow, setMoreInfoRow] = useState(null);
  const [assignmentBusyId, setAssignmentBusyId] = useState(null);
  const [locationMaps, setLocationMaps] = useState(null);

  useEffect(() => {
    let cancelled = false;
    CustomerRequestLocationService.loadCustomerRequestLocationLists()
      .then(({ provinces, cities }) => {
        if (!cancelled) {
          setLocationMaps(buildLocationLookupMaps(provinces, cities));
        }
      })
      .catch(() => {
        if (!cancelled) setLocationMaps(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const headCells = useMemo(() => {
    const cols = [
      { id: "referenceID", label: "شماره پیگیری" },
      {
        id: "createOn",
        label: "تاریخ و ساعت ایجاد",
        type: "persiandatetime",
      },
      { id: "applicantFullName", label: "نام متقاضی" },
      { id: "mobile", label: "موبایل متقاضی" },
      { id: "provinceName", label: "استان" },
      { id: "cityName", label: "شهرستان" },
      { id: "powerPlantTypeTitle", label: "نوع نیروگاه" },
      { id: "partnerReferralDisplayName", label: "معرف (سطح یک)" },
    ];

    cols.push({
      id: "leadPriorityTitle",
      label: "اولویت پیگیری",
      type: "chip",
      model: { colorField: "leadPriorityColor" },
    });

    if (showAssignmentUi) {
      cols.push(assignmentOwnerColumn);
    }

    if (!isCompactViewport) {
      cols.push({ id: "leadScore", label: "امتیاز" });
      cols.push({
        id: "followUpReminderTitle",
        label: "وضعیت پیگیری",
        type: "chip",
        model: { colorField: "followUpReminderColor" },
      });
    }

    return cols;
  }, [showAssignmentUi, isCompactViewport]);

  const onEditClicked = (row) => {
    const path = buildCheckNewRequestModifyPath(row, "solarentOperator");
    if (!path) {
      toast.error("شناسه درخواست نامعتبر است.");
      return;
    }
    openCheckNewRequestModifyPath(path);
  };

  const onFetchData = () => {
    reloadList();
  };

  const handleClaim = useCallback(
    async (row) => {
      setAssignmentBusyId(row.id);
      try {
        const res = await RequestAssignmentApi.claim(
          "SolarentOperator",
          Number(row.id)
        );
        if (res?.isSuccess) {
          toast.success(res.message || "درخواست با موفقیت دریافت شد.");
          reloadList();
        } else {
          toast.error(
            res?.message ||
            "این درخواست قبلاً توسط کارشناس دیگری دریافت شده است."
          );
          reloadList();
        }
      } catch {
        toast.error("خطا در دریافت درخواست");
      } finally {
        setAssignmentBusyId(null);
      }
    },
    [reloadList]
  );

  const handleRelease = useCallback(
    async (row) => {
      setAssignmentBusyId(row.id);
      try {
        const res = await RequestAssignmentApi.release(
          "SolarentOperator",
          Number(row.id)
        );
        if (res?.isSuccess) {
          toast.success(res.message || "درخواست آزاد شد.");
          reloadList();
        } else {
          toast.error(res?.message || "خطا در آزادسازی درخواست");
        }
      } catch {
        toast.error("خطا در آزادسازی درخواست");
      } finally {
        setAssignmentBusyId(null);
      }
    },
    [reloadList]
  );

  const rows = useMemo(
    () => enrichCheckNewRows(dataList, locationMaps),
    [dataList, locationMaps]
  );
  const hasClaimableRow = useMemo(
    () => rows.some((row) => canShowClaimAction(row, showAssignmentUi)),
    [rows, showAssignmentUi]
  );

  const powerPlantTypeOptions = useMemo(
    () =>
      [...new Set(rows.map((x) => x.powerPlantTypeTitle).filter(Boolean))].sort(),
    [rows]
  );
  const locationUsageOptions = useMemo(
    () =>
      [...new Set(rows.map((x) => x.locationUsageTypeTitle).filter(Boolean))].sort(),
    [rows]
  );
  const applicantGoalOptions = useMemo(
    () =>
      [...new Set(rows.map((x) => x.applicantGoalsTitle).filter(Boolean))].sort(),
    [rows]
  );

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      const area = Number(row.installableAreaSquareMeter);
      const passesType =
        !selectedPowerPlantType || row.powerPlantTypeTitle === selectedPowerPlantType;
      const passesUsage =
        !selectedLocationUsage || row.locationUsageTypeTitle === selectedLocationUsage;
      const passesLeadPriority =
        !selectedLeadPriority || row.leadPriorityTitle === selectedLeadPriority;
      const hasNoFollowUp = Number(row.followUpCount || 0) === 0;
      const passesFollowUpStatus =
        !selectedFollowUpStatus ||
        (selectedFollowUpStatus === "overdue" &&
          row.followUpReminderStatus === "overdue") ||
        (selectedFollowUpStatus === "today" &&
          row.followUpReminderStatus === "today") ||
        (selectedFollowUpStatus === "none" && hasNoFollowUp);
      const passesGoal =
        !selectedApplicantGoal ||
        (row.applicantGoalsTitle || "").includes(selectedApplicantGoal);
      const passesMinArea = !minArea || (!Number.isNaN(area) && area >= Number(minArea));
      const passesMaxArea = !maxArea || (!Number.isNaN(area) && area <= Number(maxArea));
      const passesPartnerReferral = matchesPartnerReferralFilter(
        row,
        partnerReferralFilter
      );
      return (
        passesType &&
        passesUsage &&
        passesLeadPriority &&
        passesFollowUpStatus &&
        passesGoal &&
        passesMinArea &&
        passesMaxArea &&
        passesPartnerReferral
      );
    });
  }, [
    rows,
    selectedPowerPlantType,
    selectedLocationUsage,
    selectedLeadPriority,
    selectedFollowUpStatus,
    selectedApplicantGoal,
    minArea,
    maxArea,
    partnerReferralFilter,
  ]);

  const tableActions = useMemo(() => {
    const actions = [];

    if (canEdit) {
      if (showAssignmentUi) {
        actions.push({
          icon: <PersonAddAlt1Icon fontSize="small" />,
          onClick: handleClaim,
          color: "success",
          tooltip: "دریافت درخواست (مسئول پیگیری می‌شوید)",
          hidden: (row) =>
            !canShowClaimAction(row, showAssignmentUi) ||
            assignmentBusyId === row.id,
        });
        actions.push({
          icon: <LockOpenIcon fontSize="small" />,
          onClick: handleRelease,
          color: "warning",
          tooltip: "آزادسازی",
          hidden: (row) => !row.canRelease || assignmentBusyId === row.id,
        });
      }
      actions.push({
        icon: <ContentPasteSearchIcon fontSize="small" />,
        onClick: onEditClicked,
        color: "primary",
        tooltip: "بررسی پرونده",
      });
      actions.push({
        icon: <InfoOutlinedIcon fontSize="small" />,
        onClick: (row) => setMoreInfoRow(row),
        color: "default",
        tooltip: "مشاهده اطلاعات",
      });
      actions.push({
        icon: <EditNoteIcon fontSize="small" />,
        onClick: (row) => setFollowUpRequestId(row.id),
        color: "secondary",
        tooltip: "ثبت پیگیری",
      });
    }

    return actions;
  }, [
    canEdit,
    showAssignmentUi,
    handleClaim,
    handleRelease,
    assignmentBusyId,
  ]);

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={12}>
        {!hubMode ? <BreadcrumbsComponent /> : null}
      </Grid>

      <Grid item xs={12} md={12}>
        <Card>
          <CardHeader
            title={pageInfo.title}
            action={
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  width: "fit-content",
                }}
              >
                <IconButton
                  variant="text"
                  color="primary"
                  onClick={onFetchData}
                  aria-label="بروزرسانی لیست"
                >
                  <SyncIcon />
                </IconButton>
              </Box>
            }
          />

          <CardContent>
            <AssignmentOperatorHelp
              showAssignmentUi={showAssignmentUi}
              featureChecked={featureChecked}
              canClaimRequests={canClaimRequests}
              currentUserKind={
                getAssignmentUserContext().currentUserKind
              }
              currentUserRoleName={currentUserRoleName}
              hasClaimableRow={hasClaimableRow}
            />
            {!canEdit ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                دسترسی «ویرایش» برای این منو فعال نیست؛ دکمه دریافت درخواست نمایش
                داده نمی‌شود.
              </Alert>
            ) : null}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              {showAssignmentUi && (
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>وضعیت مسئولیت</InputLabel>
                    <Select
                      value={assignmentFilter}
                      label="وضعیت مسئولیت"
                      onChange={(event) =>
                        setAssignmentFilter(event.target.value)
                      }
                    >
                      {assignmentFilterOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              <Grid item xs={12} sm={6} md={2}>
                <PartnerReferralFilterSelect
                  value={partnerReferralFilter}
                  onChange={setPartnerReferralFilter}
                  rows={rows}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>نوع نیروگاه</InputLabel>
                  <Select
                    value={selectedPowerPlantType}
                    label="نوع نیروگاه"
                    onChange={(event) => setSelectedPowerPlantType(event.target.value)}
                  >
                    <MenuItem value="">همه</MenuItem>
                    {powerPlantTypeOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>کاربری محل</InputLabel>
                  <Select
                    value={selectedLocationUsage}
                    label="کاربری محل"
                    onChange={(event) => setSelectedLocationUsage(event.target.value)}
                  >
                    <MenuItem value="">همه</MenuItem>
                    {locationUsageOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={6} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="حداقل مساحت"
                  type="number"
                  value={minArea}
                  onChange={(event) => setMinArea(event.target.value)}
                />
              </Grid>
              <Grid item xs={6} sm={6} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="حداکثر مساحت"
                  type="number"
                  value={maxArea}
                  onChange={(event) => setMaxArea(event.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>اولویت پیگیری</InputLabel>
                  <Select
                    value={selectedLeadPriority}
                    label="اولویت پیگیری"
                    onChange={(event) => setSelectedLeadPriority(event.target.value)}
                  >
                    <MenuItem value="">همه</MenuItem>
                    <MenuItem value="اولویت خیلی بالا">اولویت خیلی بالا</MenuItem>
                    <MenuItem value="اولویت بالا">اولویت بالا</MenuItem>
                    <MenuItem value="اولویت متوسط">اولویت متوسط</MenuItem>
                    <MenuItem value="نیاز به بررسی">نیاز به بررسی</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>هدف متقاضی</InputLabel>
                  <Select
                    value={selectedApplicantGoal}
                    label="هدف متقاضی"
                    onChange={(event) => setSelectedApplicantGoal(event.target.value)}
                  >
                    <MenuItem value="">همه</MenuItem>
                    {applicantGoalOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>وضعیت پیگیری</InputLabel>
                  <Select
                    value={selectedFollowUpStatus}
                    label="وضعیت پیگیری"
                    onChange={(event) => setSelectedFollowUpStatus(event.target.value)}
                  >
                    <MenuItem value="">همه</MenuItem>
                    <MenuItem value="overdue">پیگیری عقب افتاده</MenuItem>
                    <MenuItem value="today">پیگیری امروز</MenuItem>
                    <MenuItem value="none">بدون پیگیری</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <Grid container>
              <Grid item xs={12}>
                <EnhancedTable
                  rowKey="id"
                  rows={filteredRows}
                  headCells={headCells}
                  loading={loading}
                  tableMinWidth="auto"
                  showIndex={!isCompactViewport}
                  actionsMode={isCompactViewport ? "menu" : "inline"}
                  getRowStatus={(row) =>
                    row.followUpReminderStatus === "overdue" ? "error" : ""
                  }
                  defaultSort={{ orderBy: "createOn", order: "desc" }}
                  pageSize={100}
                  actions={tableActions}
                />
              </Grid>
            </Grid>

            <RequestFollowUpDialog
              open={!!followUpRequestId}
              onClose={() => setFollowUpRequestId(null)}
              source="SolarentOperator"
              requestInfoId={followUpRequestId}
              onSubmitted={onFetchData}
            />

            <CheckNewRequestMoreInfoModal
              open={!!moreInfoRow}
              onClose={() => setMoreInfoRow(null)}
              row={moreInfoRow}
              canEdit={canEdit}
              showAssignmentUi={showAssignmentUi}
              assignmentBusyId={assignmentBusyId}
              onClaim={handleClaim}
              onRelease={handleRelease}
              onFollowUp={(row) => setFollowUpRequestId(row.id)}
            />
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default SolarentOperatorCheckNewRequestPage;
